/**
 * The subscription flow (/subscription, /addons) can build an order for a product other
 * than the project the user is signed into: Plans & Consumption shows every product in
 * tabs, while tariff-plan and consumption are loaded for one API app at a time.
 * So the target product travels in the route and lands in
 * `subscriptionBuilder.targetProject` instead of switching app_id.
 */

/** Plans & Consumption product tab → project that owns it. */
export const PROJECT_NAME_BY_PRODUCT_KEY = {
  ltax: "LTax",
  linvoice: "LInvoice",
  lepr: "LEPR",
  lauthorize: "LAuthorize",
  // Legacy British spelling in URLs / sessionStorage
  lauthorise: "LAuthorize",
};

/** Plans Overview subscription.key → project that owns that plan. */
export const PROJECT_NAME_BY_SUBSCRIPTION_KEY = {
  vat: "LTax",
  sales_tax: "LTax",
  omp: "LTax",
  epr: "LEPR",
  invoice: "LInvoice",
  authorize: "LAuthorize",
  authorise: "LAuthorize",
};

/** Plans Overview subscription.key → /addons tabKey. */
export const ADDONS_TAB_KEY_BY_SUBSCRIPTION = {
  vat: "subscriptions",
  sales_tax: "subscriptions_sales_tax",
  omp: "subscriptions_omp",
  epr: "subscriptions_epr",
  invoice: "subscriptions_invoice",
  authorize: "subscriptions_auth",
  authorise: "subscriptions_auth",
};

/** Project → Plans & Consumption product tab key. */
export const PRODUCT_KEY_BY_PROJECT_NAME = {
  LTax: "ltax",
  LInvoice: "linvoice",
  LEPR: "lepr",
  LAuthorize: "lauthorize",
};

/** Product/project identifiers → `app` query value used by subscription APIs. */
export const SUBSCRIPTION_API_APP_BY_PRODUCT_KEY = {
  ltax: "tax",
  linvoice: "invoice",
  lepr: "epr",
  lauthorize: "authorize",
  lauthorise: "authorize",
};

export const SUBSCRIPTION_API_APP_BY_PROJECT_NAME = {
  LTax: "tax",
  LInvoice: "invoice",
  LEPR: "epr",
  LAuthorize: "authorize",
};

const SUBSCRIPTION_API_APPS = new Set([
  "tax",
  "epr",
  "invoice",
  "authorize",
]);

/**
 * Accepts an API app, Plans product key, or project name.
 * Returns null for unsupported values so callers never send a malformed query.
 */
export function resolveSubscriptionApiApp(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (SUBSCRIPTION_API_APPS.has(lower)) {
    return lower;
  }

  return (
    SUBSCRIPTION_API_APP_BY_PRODUCT_KEY[lower] ||
    SUBSCRIPTION_API_APP_BY_PROJECT_NAME[raw] ||
    null
  );
}

/**
 * /subscription/current key spellings for Authorize (API sometimes uses singular).
 * Same list as mapPlansProductData SUBSCRIPTION_KEYS_BY_KIND.authorize.
 */
export const AUTHORIZE_SUBSCRIPTION_KEYS = [
  "subscriptions_auth",
  "subscriptions_authorize",
  "subscriptions_authorise",
  "subscription_authorize",
  "subscription_auth",
];

export const CURRENT_PLAN_SUBSCRIPTION_KEYS = [
  "subscriptions",
  "subscriptions_sales_tax",
  "subscriptions_epr",
  "subscriptions_omp",
  "subscriptions_invoice",
  ...AUTHORIZE_SUBSCRIPTION_KEYS,
];

/**
 * API sends `{}` instead of null when there is no plan. Treat that like null.
 */
export function emptySubscriptionToNull(value) {
  if (value == null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return value;

  const inner =
    value.subscriptions &&
    typeof value.subscriptions === "object" &&
    !Array.isArray(value.subscriptions)
      ? value.subscriptions
      : value;

  if (
    inner &&
    typeof inner === "object" &&
    !Array.isArray(inner) &&
    Object.keys(inner).length === 0
  ) {
    return null;
  }

  return value;
}

/**
 * Active Authorize subscription block from /subscription/current, or null.
 * Unwraps nested `subscriptions` when present (same as Invoice).
 */
export function getAuthorizeSubscription(currentPlan) {
  if (!currentPlan || typeof currentPlan !== "object") return null;
  for (const key of AUTHORIZE_SUBSCRIPTION_KEYS) {
    const raw = emptySubscriptionToNull(currentPlan[key]);
    if (!raw || typeof raw !== "object") continue;
    const unwrapped =
      raw.subscriptions && typeof raw.subscriptions === "object"
        ? raw.subscriptions
        : raw;
    return emptySubscriptionToNull(unwrapped);
  }
  return null;
}

/**
 * Project the route asks the subscription flow to build an order for,
 * or null when the route says nothing (then the signed-in project is used).
 */
export function resolveTargetProjectName({ state, search } = {}) {
  const params = new URLSearchParams(search || "");
  const productKey = state?.productKey || params.get("productKey");
  const subscriptionKey = state?.subscriptionKey || params.get("subscriptionKey");

  return (
    PROJECT_NAME_BY_PRODUCT_KEY[String(productKey || "").toLowerCase()] ||
    PROJECT_NAME_BY_SUBSCRIPTION_KEY[String(subscriptionKey || "")] ||
    null
  );
}
